import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { useStream } from "@convex-dev/persistent-text-streaming/react";
import type { StreamId } from "@convex-dev/persistent-text-streaming";
import {
  ArrowUp,
  Copy,
  ExternalLink,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  Paperclip,
  Plus,
  ThumbsDown,
  ThumbsUp,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  Search,
  Settings2,
  Target,
} from "lucide-react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ui/conversation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MarkdownBody } from "@/components/ui/markdown-body";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { BADGE_TONES } from "@/lib/badge-tones";
import { formatStatusLabel } from "@/lib/format";
import {
  getInventoryStatusBadgeClass,
  getOrderStatusBadgeClass,
  getReservationStatusBadgeClass,
} from "@/lib/status-tag-colors";
import { cn } from "@/lib/utils";
import { authClient } from "@/lib/auth-client";
import { useUserProfileContext } from "@/contexts/UserProfileContext";
import type {
  CopilotAssistantPayload,
  CopilotSection,
  CopilotTimelineStep,
} from "@/types/internal-copilot";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type CopilotThread = {
  _id: string;
  title: string;
  status: "open" | "archived";
  lastMessageAt: number;
  lastMessagePreview?: string;
};

type CopilotToolEvent = {
  _id: string;
  toolName: string;
  status: "pending" | "running" | "completed" | "failed";
  startedAt?: number;
  completedAt?: number;
  error?: string;
  sourceLinks?: Array<{ label: string; href: string }>;
};

type CopilotMessage = {
  _id: string;
  role: "user" | "assistant";
  body: string;
  payload?: unknown;
  isStreaming?: boolean;
  streamId?: string;
  createdAt: number;
  toolEvents?: CopilotToolEvent[];
};

const DEFAULT_THREAD_TITLE = "New Copilot Thread";

const STREAM_URL = new URL(
  "/api/internal-copilot/stream",
  typeof window !== "undefined" ? window.location.origin : "http://localhost",
);

type CopilotDetailKind =
  | "order"
  | "client"
  | "inventory"
  | "discount"
  | "commission"
  | "trend"
  | "shipment"
  | "carrier"
  | "inventory_audit";

type CopilotDetailPanelResponse = {
  kind?: CopilotDetailKind;
  found?: boolean;
  data?: unknown;
};

type CopilotRenderMode = "default" | "chart_only_followup";
type CopilotMessageFeedback = "up" | "down";

/** Humanize raw snake_case / camelCase identifiers into readable labels */
function humanizeLabel(raw: string): string {
  return raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\bv\d+$/i, "")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/** Map known internal intent slugs to friendlier descriptions */
const INTENT_DESCRIPTIONS: Record<string, string> = {
  order_lookup: "Analyzing order activity",
  client_analysis: "Analyzing client data",
  client_change: "Analyzing client product changes",
  client_ranking: "Ranking client activity",
  inventory_check: "Checking inventory levels",
  inventory_risk: "Evaluating inventory sell-through",
  trend_analysis: "Analyzing historical trends",
  trend_overview: "Analyzing historical trends",
  shipment_tracking: "Tracking shipment status",
  commission_report: "Generating commission report",
  commission_summary: "Generating commission report",
  carrier_comparison: "Comparing carrier performance",
  revenue_breakdown: "Breaking down revenue data",
};

const TOOL_DESCRIPTIONS: Record<string, string> = {
  hybrid_pipeline_v2: "Analyzing internal records",
  client_product_history: "Reviewing client product history",
  client_product_decline: "Comparing client product decline",
  inventory_unsold_risk: "Evaluating sell-through risk",
  followup_clarification: "Clarifying follow-up scope",
  contact_support_escalation: "Escalating to support",
};

function describeIntent(intent?: string): string {
  if (!intent) return "Analyzed your request";
  const mapped = INTENT_DESCRIPTIONS[intent];
  if (mapped) return mapped;
  return humanizeLabel(intent);
}

function describeToolName(toolName?: string): string {
  if (!toolName) return "Internal analysis";
  const mapped = TOOL_DESCRIPTIONS[toolName];
  if (mapped) return mapped;
  return humanizeLabel(toolName);
}

function AgentReasoningBlock({
  planner,
  timeline,
  toolTimeline,
  toolEvents,
}: {
  planner?: any;
  timeline?: any[];
  toolTimeline?: any[];
  toolEvents?: any[];
}) {
  const [open, setOpen] = React.useState(false);

  const hasPlanner = !!planner;
  const hasTimeline = (timeline?.length ?? 0) > 0 || (toolTimeline?.length ?? 0) > 0 || (toolEvents?.length ?? 0) > 0;

  if (!hasPlanner && !hasTimeline) return null;

  const stepCount =
    (timeline?.length ?? 0) + (toolTimeline?.length ?? 0) + (toolEvents?.length ?? 0);

  return (
    <div className="mb-4 text-sm">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="group flex w-full items-center gap-2 py-1 text-muted-foreground transition-colors hover:text-foreground">
          <div className="flex size-[20px] shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 text-emerald-600 dark:text-emerald-400">
            <Check className="size-3 stroke-[3]" />
          </div>
          <span className="text-[13px] font-medium text-foreground">
            {describeIntent(planner?.intent)}
          </span>
          {planner?.timeWindow && (
            <span className="text-[12px] text-muted-foreground">
              · {planner.timeWindow}
            </span>
          )}
          {stepCount > 0 && (
            <span className="text-[12px] text-muted-foreground">
              · {stepCount} {stepCount === 1 ? "step" : "steps"}
            </span>
          )}
          {open ? <ChevronUp className="ml-auto size-3.5 opacity-50" /> : <ChevronDown className="ml-auto size-3.5 opacity-50" />}
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-2 ml-[28px] space-y-1.5 border-l border-border/60 pl-3">
          {timeline?.map((step) => (
            <div key={step.id} className="flex items-center gap-2 text-[13px] text-muted-foreground">
              {step.label.toLowerCase().includes("search") || step.label.toLowerCase().includes("query") ? (
                <Search className="size-3 shrink-0" />
              ) : (
                <Check className="size-3 shrink-0 text-emerald-500" />
              )}
              <span>{humanizeLabel(step.label)}</span>
            </div>
          ))}
          {toolTimeline?.map((tool, idx) => (
            <div key={idx} className="flex items-center gap-2 text-[13px] text-muted-foreground">
              {tool.toolName.toLowerCase().includes("search") || tool.toolName.toLowerCase().includes("query") ? (
                <Search className="size-3 shrink-0" />
              ) : (
                <Check className="size-3 shrink-0 text-emerald-500" />
              )}
              <span>{describeToolName(tool.toolName)}</span>
            </div>
          ))}
          {toolEvents?.map((tool) => (
            <div key={tool._id} className="flex items-center gap-2 text-[13px] text-muted-foreground">
              {tool.toolName.toLowerCase().includes("search") || tool.toolName.toLowerCase().includes("query") ? (
                <Search className="size-3 shrink-0" />
              ) : (
                <Check className="size-3 shrink-0 text-emerald-500" />
              )}
              <span>{describeToolName(tool.toolName)}</span>
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}


function formatTimestamp(ms: number): string {
  return new Date(ms).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function parsePayload(payload: unknown): CopilotAssistantPayload | null {
  if (!payload || typeof payload !== "object") return null;
  const asObj = payload as Record<string, unknown>;
  if (typeof asObj.answerMarkdown !== "string") return null;
  return payload as CopilotAssistantPayload;
}

function hasMetricTagSection(payload: CopilotAssistantPayload | null): boolean {
  return Boolean(payload?.sections?.some((section) => section.kind === "metric_tags"));
}

function normalizeThreadText(input: string): string {
  return input
    .trim()
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/[*_`~]+/g, "")
    .replace(/^#+\s*/g, "")
    .replace(/\s+/g, " ");
}

function truncateWithEllipsis(input: string, max: number): string {
  if (input.length <= max) return input;
  return `${input.slice(0, Math.max(0, max - 3))}...`;
}

function getThreadDisplayTitle(thread: Pick<CopilotThread, "title" | "lastMessagePreview">): string {
  const title = thread.title.trim();
  if (title && title.toLowerCase() !== DEFAULT_THREAD_TITLE.toLowerCase()) return title;

  const preview = normalizeThreadText(thread.lastMessagePreview ?? "");
  if (!preview) return title || "Untitled";

  return truncateWithEllipsis(preview, 64);
}

function toRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function toRecordArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => toRecord(entry))
    .filter((entry): entry is Record<string, unknown> => Boolean(entry));
}

function toNumeric(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;

    const parenthesizedNegative =
      trimmed.startsWith("(") && trimmed.endsWith(")");
    const normalized = (parenthesizedNegative ? trimmed.slice(1, -1) : trimmed)
      .replace(/,/g, "")
      .replace(/[^0-9.+-]/g, "");
    const parsed = Number.parseFloat(normalized);
    if (!Number.isFinite(parsed)) return 0;
    return parenthesizedNegative ? -Math.abs(parsed) : parsed;
  }
  return 0;
}

function formatMoney(value: unknown): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(toNumeric(value));
}

function formatCount(value: unknown): string {
  return Math.round(toNumeric(value)).toLocaleString();
}

function titleCaseKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function compactId(value: unknown): string {
  const text = String(value ?? "").trim();
  if (!text) return "Unknown";
  if (text.length <= 18) return text;
  return `${text.slice(0, 8)}…${text.slice(-4)}`;
}

function displayClientName(row: Record<string, unknown> | undefined, fallbackIndex: number): string {
  if (!row) return `Client ${fallbackIndex + 1}`;
  const direct =
    String(row.company ?? "").trim() ||
    String(row.clientName ?? "").trim() ||
    String(row.name ?? "").trim();
  if (direct) return direct;

  const userId = String(row.userId ?? "").trim();
  if (userId) return `Client ${compactId(userId)}`;

  return `Client ${fallbackIndex + 1}`;
}

function metricToneClass(tone: CopilotAssistantPayload["cards"][number]["tone"]): string {
  if (tone === "good") return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
  if (tone === "warn") return "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200";
  if (tone === "critical") return "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200";
  return "border-border/80 bg-muted/30 text-foreground";
}

function DetailKpi({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3 rounded-md border border-border/60 bg-background/80 px-3 py-2.5">
      <span className="truncate text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{label}</span>
      <span className="shrink-0 text-sm font-semibold leading-none text-foreground">{value}</span>
    </div>
  );
}

function ClientDetailPanel({
  panel,
}: {
  panel: CopilotDetailPanelResponse;
}) {
  const rows = toRecordArray(panel.data).sort(
    (a, b) => toNumeric(b.totalSpent) - toNumeric(a.totalSpent),
  );

  if (!panel.found || rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed p-3 text-xs text-neutral-500 dark:text-neutral-400">
        No client detail records were returned for this request.
      </div>
    );
  }

  const totalSpent = rows.reduce((sum, row) => sum + toNumeric(row.totalSpent), 0);
  const totalOrders = rows.reduce((sum, row) => sum + toNumeric(row.totalOrders), 0);
  const totalBags = rows.reduce((sum, row) => sum + toNumeric(row.totalBags), 0);
  const topClients = rows.slice(0, 8);

  const leadClient = rows[0];
  const leadProducts = toRecordArray(leadClient?.productPreferences)
    .sort((a, b) => toNumeric(b.totalSpent) - toNumeric(a.totalSpent))
    .slice(0, 6);

  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-2 gap-2">
        <DetailKpi label="Clients analyzed" value={formatCount(rows.length)} />
        <DetailKpi label="Total orders" value={formatCount(totalOrders)} />
        <DetailKpi label="Total spend" value={formatMoney(totalSpent)} />
        <DetailKpi label="Total bags" value={formatCount(totalBags)} />
      </div>

      <div className="overflow-hidden rounded-lg border border-border/70 bg-background/70">
        <div className="border-b px-2.5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          Top clients by spend
        </div>
        <div className="max-h-64 divide-y overflow-auto">
          {topClients.map((row, idx) => {
            const name = displayClientName(row, idx);
            return (
              <div key={`${String(row.userId ?? name)}-${idx}`} className="px-2.5 py-2.5">
                <p className="truncate text-sm font-medium leading-5">
                  {idx + 1}. {name}
                </p>
                <p className="mt-1.5 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                  {formatCount(row.totalOrders)} orders · {formatMoney(row.totalSpent)} spend · avg{" "}
                  {formatMoney(row.avgOrderValue)}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {leadProducts.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border/70 bg-background/70">
          <div className="border-b px-2.5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Top products for {displayClientName(leadClient, 0)}
          </div>
          <div className="max-h-52 divide-y overflow-auto">
            {leadProducts.map((product, idx) => (
              <div
                key={`${String(product.productName ?? "product")}-${idx}`}
                className="px-2.5 py-2.5"
              >
                <p className="truncate text-sm font-medium leading-5">
                  {idx + 1}. {String(product.productName ?? "Unknown product")}
                </p>
                <p className="mt-1.5 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                  {formatCount(product.totalBags)} bags · {formatMoney(product.totalSpent)}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function InventoryDetailPanel({
  panel,
}: {
  panel: CopilotDetailPanelResponse;
}) {
  if (!panel.found) {
    return (
      <div className="rounded-xl border border-dashed p-3 text-xs text-neutral-500 dark:text-neutral-400">
        No inventory detail records were returned for this request.
      </div>
    );
  }

  const payload = toRecord(panel.data);
  const velocity = toRecordArray(payload?.velocity);
  const age = toRecord(payload?.age);
  const summary = toRecord(age?.summary);
  const outliers = toRecordArray(age?.topOutliers).slice(0, 5);
  const risky = velocity
    .filter((row) => {
      const days = toNumeric(row.daysUntilZero);
      return Number.isFinite(days) && days > 0 && days < 30;
    })
    .slice(0, 8);

  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-2 gap-2">
        <DetailKpi label="Tracked cargos" value={formatCount(velocity.length)} />
        <DetailKpi label="At-risk (<30 days)" value={formatCount(risky.length)} />
        <DetailKpi label="Aging outliers" value={formatCount(summary?.outlierCount)} />
      </div>

      {risky.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border/70 bg-background/70">
          <div className="border-b px-2.5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Stockout risk watchlist
          </div>
          <div className="max-h-52 divide-y overflow-auto">
            {risky.map((row, idx) => (
              <div key={`${String(row.ociNumber ?? "cargo")}-${idx}`} className="px-2.5 py-2.5">
                <p className="truncate text-sm font-medium leading-5">{String(row.productName ?? "Unknown product")}</p>
                <p className="mt-1.5 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                  {formatCount(row.daysUntilZero)}d to zero · OCI {compactId(row.ociNumber ?? "N/A")}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {outliers.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border/70 bg-background/70">
          <div className="border-b px-2.5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            Top aging outliers
          </div>
          <div className="max-h-52 divide-y overflow-auto">
            {outliers.map((row, idx) => (
              <div key={`${String(row.ociNumber ?? "outlier")}-${idx}`} className="px-2.5 py-2.5">
                <p className="truncate text-sm font-medium leading-5">{String(row.productName ?? "Unknown product")}</p>
                <p className="mt-1.5 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                  {formatCount(row.daysInWarehouse)}d in warehouse · OCI {compactId(row.ociNumber ?? "N/A")}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ShipmentDetailPanel({
  panel,
}: {
  panel: CopilotDetailPanelResponse;
}) {
  if (!panel.found) {
    return (
      <div className="rounded-xl border border-dashed p-3 text-xs text-neutral-500 dark:text-neutral-400">
        No shipment records were returned for this request.
      </div>
    );
  }

  const payload = toRecord(panel.data);
  const summary = toRecord(payload?.summary);
  const shipment = toRecord(payload?.shipment);
  const adjustments = toRecordArray(payload?.adjustments);
  const pipeline = toRecordArray(payload?.pipeline);
  const performance = toRecordArray(payload?.deliveryPerformance);

  if (shipment) {
    return (
      <div className="space-y-3.5">
        <div className="grid grid-cols-2 gap-2">
          <DetailKpi label="Order" value={String(shipment.orderNumber ?? "Unknown")} />
          <DetailKpi label="Status" value={String(shipment.deliveryStatus ?? shipment.orderStatus ?? "unknown")} />
          <DetailKpi label="Carrier" value={String(shipment.carrierName ?? "Unknown")} />
          <DetailKpi label="Charge" value={formatMoney(shipment.deliveryCharge)} />
        </div>

        {adjustments.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-border/70 bg-background/70">
            <div className="border-b px-2.5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
              Shipping adjustments
            </div>
            <div className="max-h-48 divide-y overflow-auto">
              {adjustments.map((row, idx) => (
                <div key={idx} className="px-2.5 py-2.5">
                  <p className="text-sm font-medium">{formatMoney(row.adjustmentAmount)} adjustment</p>
                  <p className="mt-1.5 text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                    {String(row.reason ?? "No reason")} · {formatTimestamp(toNumeric(row.createdAt))}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-2 gap-2">
        <DetailKpi label="Orders in pipeline" value={formatCount(summary?.ordersInPipeline)} />
        <DetailKpi label="Delayed deliveries" value={formatCount(summary?.delayedDeliveries)} />
      </div>
      <SectionTable
        section={{ kind: "shipment_timeline", title: "Pipeline orders" }}
        rows={pipeline.length > 0 ? pipeline : performance}
      />
    </div>
  );
}

function CarrierDetailPanel({
  panel,
}: {
  panel: CopilotDetailPanelResponse;
}) {
  if (!panel.found) {
    return (
      <div className="rounded-xl border border-dashed p-3 text-xs text-neutral-500 dark:text-neutral-400">
        No carrier data was returned for this request.
      </div>
    );
  }

  const payload = toRecord(panel.data);
  const summary = toRecord(payload?.summary);
  const carriers = toRecordArray(payload?.carriers);

  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-2 gap-2">
        <DetailKpi label="Carriers" value={formatCount(summary?.carriers)} />
        <DetailKpi label="Total orders" value={formatCount(summary?.totalOrders)} />
        <DetailKpi label="Delivery spend" value={formatMoney(summary?.totalDeliveryCharge)} />
      </div>
      <SectionTable
        section={{ kind: "carrier_breakdown", title: "Carrier breakdown" }}
        rows={carriers}
      />
    </div>
  );
}

function InventoryAuditDetailPanel({
  panel,
}: {
  panel: CopilotDetailPanelResponse;
}) {
  if (!panel.found) {
    return (
      <div className="rounded-xl border border-dashed p-3 text-xs text-neutral-500 dark:text-neutral-400">
        No inventory audit records were returned for this request.
      </div>
    );
  }

  const payload = toRecord(panel.data);
  const summary = toRecord(payload?.summary);
  const watchlist = toRecordArray(payload?.watchlist);
  const reservations = toRecordArray(payload?.reservations);

  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-2 gap-2">
        <DetailKpi label="Tracked cargos" value={formatCount(summary?.trackedCargos)} />
        <DetailKpi label="Aging outliers" value={formatCount(summary?.agingOutliers)} />
        <DetailKpi label="Active reservations" value={formatCount(summary?.activeReservations)} />
      </div>

      <SectionTable
        section={{ kind: "inventory_watchlist", title: "Aging and risk watchlist" }}
        rows={watchlist}
      />
      <SectionTable
        section={{ kind: "shipment_timeline", title: "Reservations" }}
        rows={reservations}
      />
    </div>
  );
}

function GenericDetailPanel({
  panel,
}: {
  panel: CopilotDetailPanelResponse;
}) {
  if (!panel.found) {
    return (
      <div className="rounded-xl border border-dashed p-3 text-xs text-neutral-500 dark:text-neutral-400">
        No detail records were returned for this request.
      </div>
    );
  }

  const dataRecord = toRecord(panel.data);
  const dataArray = toRecordArray(panel.data);

  if (dataArray.length > 0) {
    const previewRows = dataArray.slice(0, 6);
    return (
      <div className="space-y-3.5">
        <div>
          <DetailKpi label="Rows" value={formatCount(dataArray.length)} />
        </div>
        {previewRows.map((row, idx) => {
          const scalarEntries = Object.entries(row)
            .filter(([, value]) => ["string", "number", "boolean"].includes(typeof value))
            .slice(0, 4);
          return (
            <div key={idx} className="space-y-1.5 rounded-md border border-border/70 bg-background/70 p-2.5 text-[11px]">
              {scalarEntries.length > 0 ? (
                scalarEntries.map(([key, value]) => (
                  <p key={key} className="truncate text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                    <span className="font-medium text-foreground">{titleCaseKey(key)}:</span>{" "}
                    {typeof value === "number" ? value.toLocaleString() : String(value)}
                  </p>
                ))
              ) : (
                <p className="text-neutral-500 dark:text-neutral-400">Record {idx + 1}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (dataRecord) {
    const scalarEntries = Object.entries(dataRecord).filter(([, value]) =>
      ["string", "number", "boolean"].includes(typeof value),
    );
    const aggregateEntries = Object.entries(dataRecord).filter(([, value]) => Array.isArray(value));

    return (
      <div className="space-y-3.5">
        {scalarEntries.length > 0 ? (
          <div className="space-y-1.5 rounded-md border border-border/70 bg-background/70 p-2.5">
            {scalarEntries.map(([key, value]) => (
              <p key={key} className="truncate text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                <span className="font-medium text-foreground">{titleCaseKey(key)}:</span>{" "}
                {typeof value === "number" ? value.toLocaleString() : String(value)}
              </p>
            ))}
          </div>
        ) : null}
        <div className="space-y-1.5">
          {aggregateEntries.map(([key, value]) => (
            <DetailKpi key={key} label={titleCaseKey(key)} value={formatCount((value as unknown[]).length)} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-dashed p-3 text-xs text-neutral-500 dark:text-neutral-400">
      Detail loaded, but no structured fields were available to render.
    </div>
  );
}

function DetailPanelContent({
  kind,
  panel,
}: {
  kind: CopilotDetailKind;
  panel: unknown;
}) {
  const parsed = toRecord(panel) as CopilotDetailPanelResponse | null;
  const normalized: CopilotDetailPanelResponse = {
    kind,
    found: Boolean(parsed?.found),
    data: parsed?.data,
  };

  if (kind === "client") {
    return <ClientDetailPanel panel={normalized} />;
  }
  if (kind === "inventory") {
    return <InventoryDetailPanel panel={normalized} />;
  }
  if (kind === "shipment") {
    return <ShipmentDetailPanel panel={normalized} />;
  }
  if (kind === "carrier") {
    return <CarrierDetailPanel panel={normalized} />;
  }
  if (kind === "inventory_audit") {
    return <InventoryAuditDetailPanel panel={normalized} />;
  }
  return <GenericDetailPanel panel={normalized} />;
}

function StreamingText({
  streamId,
  fallback,
}: {
  streamId: string;
  fallback: string;
}) {
  const [authToken, setAuthToken] = React.useState<string | null>(null);
  const [hasAttemptedRecovery, setHasAttemptedRecovery] = React.useState(false);
  const [isRefreshingToken, setIsRefreshingToken] = React.useState(false);

  const refreshAuthToken = React.useCallback(async (): Promise<void> => {
    try {
      const { data } = await authClient.convex.token();
      const nextToken = data?.token ?? null;
      setAuthToken(nextToken);
    } catch {
      setAuthToken(null);
    }
  }, []);

  React.useEffect(() => {
    let mounted = true;
    void (async () => {
      await refreshAuthToken();
      if (!mounted) return;
    })();

    return () => {
      mounted = false;
    };
  }, [refreshAuthToken]);

  const driven = Boolean(authToken);
  const { text, status } = useStream(
    api.internalCopilotStreaming.getStreamBody,
    STREAM_URL,
    driven,
    streamId as StreamId,
    { authToken },
  );

  const renderedText = text || fallback;
  const isStreamFailure = status === "error" || status === "timeout";

  React.useEffect(() => {
    if (!isStreamFailure) {
      setHasAttemptedRecovery(false);
      setIsRefreshingToken(false);
    }
  }, [isStreamFailure]);

  React.useEffect(() => {
    if (!isStreamFailure || renderedText || hasAttemptedRecovery) return;
    setHasAttemptedRecovery(true);
    setIsRefreshingToken(true);

    void (async () => {
      await refreshAuthToken();
      setIsRefreshingToken(false);
    })();
  }, [hasAttemptedRecovery, isStreamFailure, refreshAuthToken, renderedText]);

  if (isStreamFailure && renderedText) {
    return <MarkdownBody content={renderedText} />;
  }

  if (isStreamFailure) {
    return (
      <div className="inline-flex items-center gap-1 text-neutral-500 dark:text-neutral-400">
        <Loader2 className="size-4 animate-spin" />
        {isRefreshingToken ? "Reconnecting…" : "Still working…"}
      </div>
    );
  }

  if (status === "pending" && !renderedText) {
    return (
      <div className="inline-flex items-center gap-1 text-neutral-500 dark:text-neutral-400">
        <Loader2 className="size-4 animate-spin" />
        Thinking…
      </div>
    );
  }

  return (
    <>
      <MarkdownBody content={renderedText} />
      {status === "streaming" ? (
        <span className="ml-1 inline-block size-2 animate-pulse rounded-full bg-primary" />
      ) : null}
    </>
  );
}

function MetricCards({ cards }: { cards: CopilotAssistantPayload["cards"] }) {
  if (!cards || cards.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {cards.map((card) => (
        <Badge key={card.id} variant="outline" className={cn("px-2.5 py-0.5 text-xs font-medium", metricToneClass(card.tone))}>
          <span className="mr-1.5 opacity-60 text-[10px] uppercase tracking-wide">{card.label}</span>
          {card.value}
        </Badge>
      ))}
    </div>
  );
}

function sectionTagToneClass(
  tone?: "default" | "good" | "warn" | "critical",
): string {
  if (tone === "good") {
    return "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/30 dark:text-emerald-200";
  }
  if (tone === "warn") {
    return "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200";
  }
  if (tone === "critical") {
    return "border-red-200 bg-red-50 text-red-800 dark:border-red-800/60 dark:bg-red-950/30 dark:text-red-200";
  }
  return "border-border/70 bg-background text-foreground";
}

function formatSectionValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  const normalizedKey = key.toLowerCase();
  if (normalizedKey === "status" || normalizedKey.endsWith("status")) {
    return formatStatusLabel(String(value), "Unknown");
  }
  if (
    normalizedKey.includes("spend") ||
    normalizedKey.includes("revenue") ||
    normalizedKey.includes("amount") ||
    normalizedKey.includes("charge") ||
    normalizedKey.includes("cost")
  ) {
    return formatMoney(value);
  }
  if (normalizedKey.includes("rate")) {
    const n = toNumeric(value);
    const pct = n <= 1 ? n * 100 : n;
    return `${pct.toFixed(1)}%`;
  }
  if (
    normalizedKey.endsWith("at") ||
    normalizedKey.includes("date") ||
    normalizedKey.includes("timestamp")
  ) {
    const n = toNumeric(value);
    if (n > 0) return formatTimestamp(n);
  }
  if (typeof value === "number") return Number.isInteger(value) ? formatCount(value) : value.toFixed(2);
  return String(value);
}

function isStatusCell(key: string, value: unknown): boolean {
  if (value === null || value === undefined) return false;
  const normalizedKey = key.toLowerCase();
  if (!(normalizedKey === "status" || normalizedKey.endsWith("status"))) return false;
  return String(value).trim().length > 0;
}

function looksLikeInventoryStatus(status: unknown): boolean {
  const normalized = String(status ?? "").trim().toLowerCase();
  return (
    normalized === "at_origin" ||
    normalized === "in_transit" ||
    normalized === "in_spot" ||
    normalized === "sold_out" ||
    normalized === "upcoming" ||
    normalized === "archived" ||
    normalized === "available" ||
    normalized === "allocated" ||
    normalized === "reserved"
  );
}

function resolveStatusBadgeClass(
  sectionKind: CopilotSection["kind"],
  header: string,
  value: unknown,
): string {
  const key = header.toLowerCase();
  if (key.includes("reservation")) {
    return getReservationStatusBadgeClass(value);
  }
  if (sectionKind === "inventory_watchlist" || looksLikeInventoryStatus(value)) {
    return getInventoryStatusBadgeClass(value);
  }
  return getOrderStatusBadgeClass(value);
}

function compactHeaders(rows: Array<Record<string, unknown>>): string[] {
  if (rows.length === 0) return [];
  const preferred = [
    "rank",
    "client",
    "carrier",
    "product",
    "orderNumber",
    "orderStatus",
    "deliveryStatus",
    "orderCount",
    "totalSpend",
    "avgOrderValue",
    "daysUntilZero",
    "availableBags",
    "onTimeRate",
    "totalDeliveryCharge",
  ];
  const keys = Object.keys(rows[0] ?? {});
  const ordered = [
    ...preferred.filter((key) => keys.includes(key)),
    ...keys.filter((key) => !preferred.includes(key)),
  ];
  return ordered.slice(0, 6);
}

function SectionTable({
  section,
  rows,
}: {
  section: CopilotSection;
  rows: Array<Record<string, unknown>>;
}) {
  if (rows.length === 0) return null;
  const headers = compactHeaders(rows);
  const rowLimit =
    typeof section.maxRows === "number" && Number.isFinite(section.maxRows)
      ? Math.max(1, Math.min(120, Math.floor(section.maxRows)))
      : 12;

  return (
    <div className="overflow-hidden rounded-xl border border-border/70 bg-background/90">
      <div className="border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        {section.title || titleCaseKey(section.kind)}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-sm">
          <thead className="bg-neutral-50/60 dark:bg-neutral-900/40">
            <tr>
              {headers.map((header) => (
                <th
                  key={header}
                  className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400"
                >
                  {titleCaseKey(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.slice(0, rowLimit).map((row, idx) => (
              <tr key={idx}>
                {headers.map((header) => {
                  const value = row[header];
                  const formatted = formatSectionValue(header, value);
                  const statusCell = isStatusCell(header, value);
                  return (
                    <td key={header} className="px-3 py-2 align-top">
                      {statusCell ? (
                        <Badge
                          variant="outline"
                          className={cn(
                            "max-w-[280px] truncate px-2 py-0.5 text-xs font-medium shadow-none",
                            resolveStatusBadgeClass(section.kind, header, value),
                          )}
                        >
                          {formatted}
                        </Badge>
                      ) : (
                        <span className="block max-w-[280px] truncate">{formatted}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SectionsBlock({
  sections,
  renderMode = "default",
}: {
  sections?: CopilotSection[];
  renderMode?: CopilotRenderMode;
}) {
  if (!sections || sections.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {sections.map((section, idx) => {
        if (
          renderMode === "chart_only_followup" &&
          (section.kind === "ranking_table" ||
            section.kind === "shipment_timeline" ||
            section.kind === "inventory_watchlist" ||
            section.kind === "carrier_breakdown")
        ) {
          return null;
        }

        const rows = toRecordArray(section.rows);
        if (section.kind === "metric_tags") {
          const tags = Array.isArray(section.tags) ? section.tags : [];
          if (tags.length === 0) return null;
          return (
            <div key={`${section.kind}-${idx}`} className="flex flex-wrap items-center gap-2">
              {tags.map((tag, tagIdx) => (
                <Badge
                  key={`${tag.label}-${tagIdx}`}
                  variant="outline"
                  className={cn(
                    "rounded-full px-2.5 py-1 text-xs font-medium shadow-none",
                    sectionTagToneClass(tag.tone),
                  )}
                >
                  <span className="mr-1.5 text-[10px] uppercase tracking-wide opacity-70">{tag.label}</span>
                  {tag.value}
                </Badge>
              ))}
            </div>
          );
        }

        if (section.kind === "notes" && section.markdown) {
          return (
            <div
              key={`${section.kind}-${idx}`}
              className="rounded-xl border border-border/70 bg-background/90 px-3 py-2"
            >
              <MarkdownBody content={section.markdown} />
            </div>
          );
        }

        if (
          section.kind === "ranking_table" ||
          section.kind === "shipment_timeline" ||
          section.kind === "inventory_watchlist" ||
          section.kind === "carrier_breakdown"
        ) {
          return <SectionTable key={`${section.kind}-${idx}`} section={section} rows={rows} />;
        }

        if (rows.length > 0) {
          return <SectionTable key={`${section.kind}-${idx}`} section={section} rows={rows} />;
        }

        if (section.markdown) {
          return (
            <div
              key={`${section.kind}-${idx}`}
              className="rounded-xl border border-border/70 bg-background/90 px-3 py-2"
            >
              <MarkdownBody content={section.markdown} />
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

function ChartBlock({ chart }: { chart: CopilotAssistantPayload["chart"] }) {
  if (!chart || !Array.isArray(chart.data) || chart.data.length === 0) return null;

  return (
    <Card className="border-border/70 bg-background/70 shadow-none">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{chart.title}</CardTitle>
      </CardHeader>
      <CardContent className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === "bar" ? (
            <BarChart data={chart.data}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-40" />
              <XAxis dataKey={chart.xKey} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              {chart.series.map((serie, idx) => (
                <Bar
                  key={serie.key}
                  dataKey={serie.key}
                  fill={idx === 0 ? "#0ea5e9" : "#6366f1"}
                  radius={[3, 3, 0, 0]}
                />
              ))}
            </BarChart>
          ) : (
            <LineChart data={chart.data}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-40" />
              <XAxis dataKey={chart.xKey} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              {chart.series.map((serie, idx) => (
                <Line
                  key={serie.key}
                  type="monotone"
                  dataKey={serie.key}
                  stroke={idx === 0 ? "#0ea5e9" : "#6366f1"}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export default function AdminCopilotPage() {
  const {
    isAuthenticated,
    isAuthBridgeReady,
    isConvexAuthenticated,
    isConvexLoading,
  } = useUserProfileContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedThreadId = searchParams.get("threadId") ?? "";
  const canUseCopilotQueries = isAuthenticated && isAuthBridgeReady;
  const [bridgeRetryAttempt, setBridgeRetryAttempt] = React.useState(0);

  React.useEffect(() => {
    if (!isAuthenticated || canUseCopilotQueries) {
      if (bridgeRetryAttempt !== 0) {
        setBridgeRetryAttempt(0);
      }
      return;
    }

    const delayMs = Math.min(8_000, 1_000 * 2 ** Math.min(bridgeRetryAttempt, 3));
    const timeout = window.setTimeout(() => {
      void authClient.convex.token().catch(() => {
        // silent bridge retry
      });
      setBridgeRetryAttempt((attempt) => attempt + 1);
    }, delayMs);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [bridgeRetryAttempt, canUseCopilotQueries, isAuthenticated]);

  const threads = useQuery(
    api.internalCopilot.listThreads,
    canUseCopilotQueries
      ? {
          limit: 100,
          includeArchived: false,
        }
      : "skip",
  ) as CopilotThread[] | undefined;

  React.useEffect(() => {
    if (!canUseCopilotQueries) return;
    if (!threads || threads.length === 0) return;
    if (selectedThreadId) return;
    setSearchParams({ threadId: threads[0]!._id }, { replace: true });
  }, [canUseCopilotQueries, threads, selectedThreadId, setSearchParams]);

  const threadData = useQuery(
    api.internalCopilot.getThread,
    canUseCopilotQueries && selectedThreadId
      ? { threadId: selectedThreadId as any, limit: 300 }
      : "skip",
  ) as { thread: CopilotThread; messages: CopilotMessage[] } | undefined;

  const sendMessage = useMutation(api.internalCopilot.sendMessage);
  const createThread = useMutation(api.internalCopilot.createThread);
  const archiveThread = useMutation(api.internalCopilot.archiveThread);

  const [detailRequest, setDetailRequest] = React.useState<
    | {
        kind:
          | "order"
          | "client"
          | "discount"
          | "inventory"
          | "commission"
          | "trend"
          | "shipment"
          | "carrier"
          | "inventory_audit";
        entityId?: string;
        dateRange?: "30d" | "90d" | "6mo" | "1y" | "all";
      }
    | null
  >(null);

  const detailPanel = useQuery(
    api.internalCopilot.getDetailPanel,
    canUseCopilotQueries && detailRequest ? (detailRequest as any) : "skip",
  );

  const [draft, setDraft] = React.useState("");
  const [isSending, setIsSending] = React.useState(false);
  const [isCreatingThread, setIsCreatingThread] = React.useState(false);
  const [messageFeedback, setMessageFeedback] = React.useState<
    Record<string, CopilotMessageFeedback>
  >({});

  const handleCopyResponse = React.useCallback(async (body: string) => {
    const fullText = body.trim();
    if (!fullText) return;
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      toast.error("Copy is not available in this browser.");
      return;
    }

    try {
      await navigator.clipboard.writeText(fullText);
      toast.success("Response copied.");
    } catch {
      toast.error("Could not copy response.");
    }
  }, []);

  const handleToggleFeedback = React.useCallback(
    (messageId: string, feedback: CopilotMessageFeedback) => {
      setMessageFeedback((current) => {
        if (current[messageId] === feedback) {
          const next = { ...current };
          delete next[messageId];
          return next;
        }
        return {
          ...current,
          [messageId]: feedback,
        };
      });
    },
    [],
  );

  const handleCreateThread = React.useCallback(async () => {
    if (!canUseCopilotQueries || isCreatingThread) return;
    setIsCreatingThread(true);
    try {
      const result = await createThread({});
      setSearchParams({ threadId: result.threadId as string });
      setDetailRequest(null);
    } catch {
      // no-op, surfaced via disabled UI state fallback.
    } finally {
      setIsCreatingThread(false);
    }
  }, [canUseCopilotQueries, createThread, isCreatingThread, setSearchParams]);

  const handleArchiveThread = React.useCallback(async () => {
    if (!canUseCopilotQueries) return;
    if (!selectedThreadId) return;
    try {
      await archiveThread({ threadId: selectedThreadId as any, status: "archived" });
      setSearchParams({}, { replace: true });
      setDetailRequest(null);
      toast.success("Thread archived.");
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, "Couldn't archive thread. Please try again."),
      );
    }
  }, [archiveThread, canUseCopilotQueries, selectedThreadId, setSearchParams]);

  const handleSend = React.useCallback(async () => {
    if (!canUseCopilotQueries) return;
    const body = draft.trim();
    if (!body || isSending) return;

    setIsSending(true);
    try {
      const result = await sendMessage(
        selectedThreadId
          ? { threadId: selectedThreadId as any, body }
          : { body },
      );

      setDraft("");
      setDetailRequest(null);
      if (!selectedThreadId || result.threadId !== selectedThreadId) {
        setSearchParams({ threadId: result.threadId as string });
      }
    } catch {
      // silent retry path handles transient auth bridge issues
    } finally {
      setIsSending(false);
    }
  }, [canUseCopilotQueries, draft, isSending, selectedThreadId, sendMessage, setSearchParams]);

  const handleSelectThread = React.useCallback(
    (threadId: string) => {
      if (!canUseCopilotQueries) return;
      setSearchParams({ threadId });
      setDetailRequest(null);
    },
    [canUseCopilotQueries, setSearchParams],
  );

  const messages = threadData?.messages ?? [];
  const latestAssistantMessage = React.useMemo(() => {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      const message = messages[idx];
      if (message?.role === "assistant") return message;
    }
    return null;
  }, [messages]);
  const latestAssistantMessageId = latestAssistantMessage?._id ?? null;

  const showDetailPanel = Boolean(detailRequest);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Internal Copilot</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Ask internal analytics questions with evidence, links, and tool timeline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={!selectedThreadId || !canUseCopilotQueries}
              >
                Archive thread
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive this thread?</AlertDialogTitle>
                <AlertDialogDescription>
                  The thread will be hidden from your active list. You can still recover it from the archive if you change your mind.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep open</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchiveThread}>
                  Archive thread
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button
            size="sm"
            onClick={handleCreateThread}
            disabled={!canUseCopilotQueries || isCreatingThread}
            aria-busy={isCreatingThread}
          >
            {isCreatingThread ? (
              <Loader2 className="mr-1 size-4 animate-spin" />
            ) : (
              <Plus className="mr-1 size-4" />
            )}
            {isCreatingThread ? "Creating..." : "New thread"}
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden rounded-2xl border bg-background">
        <div className="grid h-full min-h-0 lg:grid-cols-[280px_minmax(0,1fr)]">
          <section className="flex min-h-0 flex-col border-r bg-neutral-50/40 dark:bg-neutral-950/20">
            <div className="border-b px-3 py-2 text-sm font-medium">Threads</div>
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {!canUseCopilotQueries || threads === undefined ? (
                <div className="space-y-2" aria-hidden="true">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <Skeleton key={idx} className="h-14 w-full rounded-lg" />
                  ))}
                </div>
              ) : threads.length === 0 ? (
                <div className="rounded-xl border border-dashed p-4 text-center text-sm text-neutral-500 dark:text-neutral-400">
                  No threads yet. Start by sending a question.
                </div>
              ) : (
                <div className="space-y-1">
                  {threads.map((thread) => (
                    <button
                      key={thread._id}
                      type="button"
                      className={cn(
                        "w-full rounded-lg border px-3 py-2 text-left transition-colors",
                        thread._id === selectedThreadId
                          ? "border-border bg-background"
                          : "border-transparent hover:border-border hover:bg-background/60",
                      )}
                      onClick={() => handleSelectThread(thread._id)}
                    >
                      <p className="truncate text-sm font-medium">{getThreadDisplayTitle(thread)}</p>
                      <p className="mt-0.5 truncate text-xs text-neutral-500 dark:text-neutral-400">
                        {thread.lastMessagePreview || "No messages"}
                      </p>
                      <p className="mt-1 text-[10px] uppercase text-neutral-500 dark:text-neutral-400">
                        {formatTimestamp(thread.lastMessageAt)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="flex min-h-0 flex-col">
            <div className="border-b px-4 py-2 text-sm font-medium">
              {!canUseCopilotQueries
                ? "Reconnecting to internal data…"
                : threadData?.thread
                ? getThreadDisplayTitle({
                    title: threadData.thread.title,
                    lastMessagePreview: threadData.thread.lastMessagePreview,
                  })
                : "New conversation"}
            </div>

            <div
              className={cn(
                "grid min-h-0 flex-1 gap-0",
                showDetailPanel ? "lg:grid-cols-[minmax(0,1fr)_340px]" : "lg:grid-cols-1",
              )}
            >
              <div className="flex min-h-0 flex-col">
                <div className="min-h-0 flex-1">
                  <Conversation className="h-full">
                    <ConversationContent className="mx-auto w-full max-w-4xl space-y-6 px-5 py-6">
                      {!canUseCopilotQueries ? (
                        <div className="space-y-3">
                          <Skeleton className="h-14 w-[70%]" />
                          <Skeleton className="ml-auto h-14 w-[60%]" />
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">
                            Re-syncing authentication bridge
                            {isConvexLoading ? "…" : "."}
                          </p>
                        </div>
                      ) : selectedThreadId && threadData === undefined ? (
                        <div className="space-y-3">
                          <Skeleton className="h-14 w-[70%]" />
                          <Skeleton className="ml-auto h-14 w-[60%]" />
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="flex h-full min-h-[250px] items-center justify-center">
                          <div className="max-w-sm rounded-xl border border-dashed p-5 text-center text-sm text-neutral-500 dark:text-neutral-400">
                            <MessageSquare className="mx-auto mb-3 size-5" />
                            Ask about orders, clients, inventory, commissions, or trends.
                          </div>
                        </div>
                      ) : (
                        messages.map((message) => {
                          const assistantPayload = parsePayload(message.payload);
                          const assistantBody = assistantPayload?.answerMarkdown || message.body;
                          const alignUser = message.role === "user";
                          const detailActions = assistantPayload?.detailActions ?? [];
                          const hasDetailActions = detailActions.length > 0;
                          const hasTimeline =
                            (assistantPayload?.timeline?.length ?? 0) > 0 ||
                            (message.toolEvents?.length ?? 0) > 0;
                          const executedTools =
                            (assistantPayload?.timeline?.length ?? 0) > 0
                              ? assistantPayload!.timeline.map((step) => ({
                                  id: step.id,
                                  label: describeToolName(step.label),
                                  status: step.status,
                                }))
                              : (message.toolEvents ?? []).map((event) => ({
                                  id: event._id,
                                  label: describeToolName(event.toolName),
                                  status: event.status,
                                }));
                          const isLatestAssistantMessage =
                            !alignUser && message._id === latestAssistantMessageId;
                          const showAssistantActions =
                            isLatestAssistantMessage && !message.isStreaming;

                          return (
                            <div
                              key={message._id}
                              className={cn(
                                "flex w-full",
                                alignUser ? "justify-end" : "justify-start",
                              )}
                            >
                              <div
                                className={cn(
                                  "space-y-3",
                                  alignUser
                                    ? "max-w-[70%] rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm leading-6 text-foreground dark:border-neutral-800 dark:bg-background"
                                    : "w-full max-w-3xl text-[15px] leading-7 text-foreground",
                                )}
                              >
                                {alignUser ? (
                                  <MarkdownBody content={message.body} />
                                ) : (
                                  <>
                                    <AgentReasoningBlock
                                      planner={assistantPayload?.planner}
                                      timeline={assistantPayload?.timeline}
                                      toolTimeline={assistantPayload?.toolTimeline}
                                      toolEvents={message.toolEvents}
                                    />
                                    {message.isStreaming && message.streamId ? (
                                      <StreamingText
                                        streamId={message.streamId}
                                        fallback={message.body}
                                      />
                                    ) : (
                                      <MarkdownBody content={assistantBody} />
                                    )}
                                  </>
                                )}

                                {!alignUser && assistantPayload ? (
                                  <div className="flex flex-col gap-4 pt-2">
                                    <SectionsBlock
                                      sections={assistantPayload.sections}
                                      renderMode={(assistantPayload as any).renderHints?.mode ?? "default"}
                                    />
                                    {!hasMetricTagSection(assistantPayload) ? (
                                      <MetricCards cards={assistantPayload.cards} />
                                    ) : null}
                                    <ChartBlock chart={assistantPayload.chart} />

                                    {assistantPayload.sources.length > 0 && (
                                      <div className="flex flex-col gap-4 border-t border-border/60 pt-4">
                                        {assistantPayload.sources.length > 0 && (
                                          <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-xs font-medium text-muted-foreground">Sources</span>
                                            {assistantPayload.sources.map((source, idx) => (
                                              <a
                                                key={`${source.href}-${idx}`}
                                                href={source.href}
                                                className="inline-flex items-center gap-1 rounded-md bg-secondary/60 px-2 py-1 text-xs font-medium text-secondary-foreground transition-colors hover:bg-secondary"
                                              >
                                                {source.label}
                                                <ExternalLink className="size-3 opacity-70" />
                                              </a>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ) : null}

                                {showAssistantActions ? (
                                  <div className="mt-3 flex flex-wrap items-center gap-1 text-muted-foreground">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="size-8 rounded-full"
                                      aria-label="Copy response"
                                      title="Copy response"
                                      onClick={() =>
                                        void handleCopyResponse(assistantBody)
                                      }
                                      disabled={!assistantBody.trim()}
                                    >
                                      <Copy className="size-4" />
                                    </Button>
                                    {hasDetailActions ? (
                                      detailActions.length === 1 ? (
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="size-8 rounded-full"
                                          aria-label={detailActions[0]!.label}
                                          title={detailActions[0]!.label}
                                          onClick={() =>
                                            setDetailRequest({
                                              kind: detailActions[0]!.kind,
                                              entityId: detailActions[0]!.entityId,
                                              dateRange: detailActions[0]!.dateRange,
                                            })
                                          }
                                        >
                                          <ExternalLink className="size-4" />
                                        </Button>
                                      ) : (
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              className="size-8 rounded-full"
                                              aria-label="Open detail actions"
                                              title="Open detail actions"
                                            >
                                              <ExternalLink className="size-4" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="start">
                                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                              Detail actions
                                            </DropdownMenuLabel>
                                            {detailActions.map((action) => (
                                              <DropdownMenuItem
                                                key={action.id}
                                                onSelect={() =>
                                                  setDetailRequest({
                                                    kind: action.kind,
                                                    entityId: action.entityId,
                                                    dateRange: action.dateRange,
                                                  })
                                                }
                                              >
                                                {action.label}
                                              </DropdownMenuItem>
                                            ))}
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      )
                                    ) : null}
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className={cn(
                                        "size-8 rounded-full",
                                        messageFeedback[message._id] ===
                                          "up" &&
                                          BADGE_TONES.emerald,
                                      )}
                                      aria-label="Mark response helpful"
                                      title="Mark response helpful"
                                      onClick={() =>
                                        handleToggleFeedback(
                                          message._id,
                                          "up",
                                        )
                                      }
                                    >
                                      <ThumbsUp className="size-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className={cn(
                                        "size-8 rounded-full",
                                        messageFeedback[message._id] ===
                                          "down" &&
                                          BADGE_TONES.amber,
                                      )}
                                      aria-label="Mark response unhelpful"
                                      title="Mark response unhelpful"
                                      onClick={() =>
                                        handleToggleFeedback(
                                          message._id,
                                          "down",
                                        )
                                      }
                                    >
                                      <ThumbsDown className="size-4" />
                                    </Button>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="size-8 rounded-full"
                                          aria-label="More response actions"
                                          title="More response actions"
                                        >
                                          <MoreHorizontal className="size-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="start" className="w-64">
                                        <DropdownMenuItem
                                          onSelect={() =>
                                            void handleCopyResponse(
                                              assistantBody,
                                            )
                                          }
                                        >
                                          Copy response
                                        </DropdownMenuItem>
                                        {hasDetailActions ? (
                                          <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                              Detail actions
                                            </DropdownMenuLabel>
                                            {detailActions.map((action) => (
                                              <DropdownMenuItem
                                                key={action.id}
                                                onSelect={() =>
                                                  setDetailRequest({
                                                    kind: action.kind,
                                                    entityId: action.entityId,
                                                    dateRange: action.dateRange,
                                                  })
                                                }
                                              >
                                                {action.label}
                                              </DropdownMenuItem>
                                            ))}
                                          </>
                                        ) : null}
                                        {hasTimeline ? (
                                          <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                              Executed tools
                                            </DropdownMenuLabel>
                                            {executedTools.map((tool) => (
                                              <DropdownMenuItem
                                                key={tool.id}
                                                className="flex items-center justify-between gap-2"
                                              >
                                                <span className="truncate">
                                                  {tool.label}
                                                </span>
                                                <Badge
                                                  variant="outline"
                                                  className={cn(
                                                    "text-[10px] uppercase tracking-wide",
                                                    tool.status ===
                                                      "completed"
                                                      ? BADGE_TONES.emerald
                                                      : tool.status ===
                                                          "failed"
                                                        ? BADGE_TONES.red
                                                        : BADGE_TONES.sky,
                                                  )}
                                                >
                                                  {tool.status}
                                                </Badge>
                                              </DropdownMenuItem>
                                            ))}
                                          </>
                                        ) : null}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </ConversationContent>
                    <ConversationScrollButton />
                  </Conversation>
                </div>

                <div className="bg-background px-4 py-4">
                  <div className="mx-auto w-full max-w-4xl">
                    <div className="rounded-[2rem] border border-border/70 bg-background/95 px-3 py-2 shadow-sm backdrop-blur">
                      <div className="flex items-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="mb-1 size-8 rounded-full text-muted-foreground"
                          aria-label="Attach context"
                          title="Attach context"
                          onClick={() =>
                            toast.info("Attachment support is coming soon.")
                          }
                        >
                          <Paperclip className="size-4" />
                        </Button>

                        <Textarea
                          value={draft}
                          onChange={(event) => setDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" && !event.shiftKey) {
                              event.preventDefault();
                              void handleSend();
                            }
                          }}
                          placeholder="Ask internal analytics questions..."
                          className="min-h-[36px] max-h-36 flex-1 resize-none border-0 bg-transparent px-0 py-2 text-base shadow-none placeholder:text-muted-foreground focus-visible:ring-0"
                          disabled={!canUseCopilotQueries}
                        />

                        <Button
                          type="button"
                          size="icon"
                          className="mb-1 size-9 rounded-full bg-black text-white shadow-sm hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
                          aria-label="Send"
                          title="Send"
                          onClick={() => void handleSend()}
                          disabled={isSending || draft.trim().length === 0 || !canUseCopilotQueries}
                        >
                          {isSending ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <ArrowUp className="size-4" />
                          )}
                        </Button>
                      </div>

                      {!canUseCopilotQueries ? (
                        <p className="pb-1 text-xs text-neutral-500 dark:text-neutral-400">
                          Reconnecting secure admin bridge
                          {isConvexAuthenticated ? "…" : ` (retry ${bridgeRetryAttempt + 1})…`}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              <aside
                className={cn(
                  "hidden min-h-0 border-l bg-neutral-50/40 dark:bg-neutral-950/20 lg:flex lg:flex-col",
                  !showDetailPanel && "lg:hidden",
                )}
              >
                <div className="border-b px-3 py-2 text-sm font-medium">Detail panel</div>
                <div className="min-h-0 flex-1 overflow-y-auto p-4">
                  {!detailRequest ? (
                    <div className="rounded-xl border border-dashed p-3 text-xs text-neutral-500 dark:text-neutral-400">
                      Use a detail action from the latest assistant response to inspect scoped internal records.
                    </div>
                  ) : !canUseCopilotQueries || detailPanel === undefined ? (
                    <div className="space-y-2" aria-hidden="true">
                      <Skeleton className="h-5 w-28" />
                      <Skeleton className="h-24 w-full" />
                      <Skeleton className="h-24 w-full" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                          {detailRequest.kind} detail
                        </p>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setDetailRequest(null)}>
                          Close
                        </Button>
                      </div>
                      <DetailPanelContent
                        kind={detailRequest.kind}
                        panel={detailPanel}
                      />
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
